import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { PlatformRole, Scope } from '@shiocast/contracts';
import { request, setAccessToken, setCurrentStation } from '@/api/client';

interface AuthenticatedResponse {
  status: 'authenticated';
  access_token: string;
  expires_in: number;
  user: {
    id: string;
    email: string;
    display_name: string | null;
    role: PlatformRole;
    scopes: Scope[];
  };
}

interface TotpChallengeResponse {
  status: 'totp_required';
  challenge_token: string;
  expires_in: number;
}

type LoginResponse = AuthenticatedResponse | TotpChallengeResponse;

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthenticatedResponse['user'] | null>(null);
  const scopes = ref<Set<Scope>>(new Set());
  const totpChallenge = ref<string | null>(null);
  const initialising = ref(true);
  /** The one restore attempt per page load, shared by everyone who awaits it. */
  let restoreInFlight: Promise<void> | null = null;

  const isAuthenticated = computed(() => user.value !== null);

  /** Authorisation in the panel mirrors the API. Route visibility is not a check. */
  function can(scope: Scope): boolean {
    return scopes.value.has(scope);
  }

  function adopt(response: AuthenticatedResponse): void {
    setAccessToken(response.access_token);
    user.value = response.user;
    scopes.value = new Set(response.user.scopes);
    totpChallenge.value = null;
  }

  function clear(): void {
    setAccessToken(null);
    setCurrentStation(null);
    user.value = null;
    scopes.value = new Set();
    totpChallenge.value = null;
  }

  async function login(
    email: string,
    password: string,
    trustDevice: boolean,
  ): Promise<'ok' | 'totp'> {
    const response = await request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: { email, password, trustDevice },
      skipRefresh: true,
    });

    if (response.status === 'totp_required') {
      totpChallenge.value = response.challenge_token;
      return 'totp';
    }

    adopt(response);
    return 'ok';
  }

  async function submitTotp(code: string): Promise<void> {
    if (!totpChallenge.value) throw new Error('There is no sign-in attempt to complete.');
    const response = await request<AuthenticatedResponse>('/auth/totp', {
      method: 'POST',
      body: { challengeToken: totpChallenge.value, code },
      skipRefresh: true,
    });
    adopt(response);
  }

  /**
   * The same step, with a recovery code instead of a live one. The code is sent as the
   * person typed it; the API is the one that knows a dash and lower case are the same
   * code, and duplicating that rule here would let the two disagree.
   */
  async function submitRecoveryCode(code: string): Promise<void> {
    if (!totpChallenge.value) throw new Error('There is no sign-in attempt to complete.');
    const response = await request<AuthenticatedResponse>('/auth/recovery', {
      method: 'POST',
      body: { challengeToken: totpChallenge.value, code },
      skipRefresh: true,
    });
    adopt(response);
  }

  /**
   * Restore a session on page load from the refresh cookie. A failure here is the
   * ordinary "not signed in" case, not an error worth showing.
   *
   * Memoised: the router guard and anything else that asks during boot share one call,
   * so the refresh token is rotated once rather than tripping reuse detection.
   */
  function restore(): Promise<void> {
    if (!initialising.value) return Promise.resolve();
    restoreInFlight ??= (async () => {
      try {
        const response = await request<AuthenticatedResponse>('/auth/refresh', {
          method: 'POST',
          body: {},
          skipRefresh: true,
        });
        adopt(response);
      } catch {
        clear();
      } finally {
        initialising.value = false;
        restoreInFlight = null;
      }
    })();
    return restoreInFlight;
  }

  async function logout(): Promise<void> {
    try {
      await request<void>('/auth/logout', { method: 'POST' });
    } finally {
      clear();
    }
  }

  return {
    user,
    scopes,
    totpChallenge,
    initialising,
    isAuthenticated,
    can,
    login,
    submitTotp,
    submitRecoveryCode,
    restore,
    logout,
    clear,
  };
});
